import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Plug, Terminal, BookOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import { STATES, describeApp, byUrgency } from '../utils/appState';
import { listAllSchemas } from '../utils/schemaCatalog';
import { loadFleetSignals, factsFor } from '../utils/fleetFacts';
import PayoutChecklist from '../components/dev/PayoutChecklist';
import useLatest from '../utils/useLatest';
import { STEPS, readOnboarding } from '../utils/onboarding';
import api from '../services/api';
import './DeveloperHome.css';

/**
 * THE PORTAL'S HOME — the page behind the rail's mark.
 *
 * Nucleus's Home is one picture (the vault); this one is too: every app you
 * own, placed along the five states it moves through — Designing, Registered,
 * Testing, Live, Charging — so where the fleet stands reads at a glance. Under
 * it, NEEDS YOU names each app's one blocker with the press that fixes it;
 * then the figures, GETTING PAID (the payout account as a checklist), and the
 * ways to start something.
 *
 * THE DERIVATION IS NOT HERE. `utils/appState.js` decides state and blocker,
 * and an app's own header calls the same function, so the two can never
 * disagree about the same app.
 *
 * THREE FLEET CALLS, THEN ONE PER APP. my-apps, my-apps/overview and
 * my-apps/scores are each a single request for the whole fleet. The draft is
 * per-app with no batch route, so those go out in parallel and every one may
 * fail: a draft we could not read means no marker, never a page that won't load.
 *
 * ONLY THE CURRENT SPACE LANDS. Every load is tagged (utils/useLatest.js):
 * switch from Personal to a group while Personal is still loading, and
 * Personal's apps and figures are dropped instead of showing under the group.
 *
 * AN EMPTY SPACE GOES TO /welcome until they skip it or something registers.
 * Nothing built here (no apps, no plugins) and onboarding neither finished nor
 * skipped (utils/onboarding.js) sends every visit to Home on to it — Skip is on
 * every screen, so that is a nudge, not a wall. A new member of a group that already has apps never
 * sees it, because the group is not empty. Anything unknown — the plugin list
 * failed, storage threw — stays on Home: being stuck in a welcome is worse
 * than missing one.
 */

const PIPELINE = [STATES.designing, STATES.registered, STATES.testing, STATES.live, STATES.charging];
const ABOUT = {
  designing: 'No design doc yet.',
  registered: 'Has keys. Declares what it needs.',
  testing: 'Sandbox users are trying it.',
  live: 'Real people have let it in.',
  charging: 'Live, priced and getting paid.',
};

/** Schemas the active owner made: the group's when in a group, else yours and no group's — DevSchemasTab's "My schemas". */
export const ownSchemas = (schemas, userId, groupId) => (schemas || []).filter((s) => (groupId
  ? s.group_id === groupId
  : userId != null && s.owner_id === userId && !s.group_id));

const isPaid = (app) => !!app?.pricing?.model && app.pricing.model !== 'free';
const appHref = (row) => (row.isPlugin ? `/plugins/${row.id}` : `/applications/${row.id}`);

/** A figure on the stats sheet; a button only when it has somewhere useful to go. */
function Figure({ value, label, note, onPress }) {
  const shown = value === undefined || value === null ? '—' : value;
  const dead = !onPress || !value;
  return (
    <button type="button" className="devhome__figure" disabled={dead} onClick={dead ? undefined : onPress}>
      <span className="devhome__figure-lbl">{label}</span>
      <span className="devhome__figure-val">{shown}</span>
      {note && <span className="devhome__figure-note">{note}</span>}
    </button>
  );
}

export default function DeveloperHome() {
  const navigate = useNavigate();
  const { activeGroupId } = useGroup();
  const { user } = useAuth();
  const userId = user?.id;
  const [rows, setRows] = useState(null);      // null = not loaded yet
  // Whether the authorization counts came in. When /my-apps/overview fails
  // every app derives to Registered for want of a count, so — as the Apps list
  // does (AppFleet) — a stage above Designing reads as unknown, not Registered.
  const [usersKnown, setUsersKnown] = useState(true);
  const [priced, setPriced] = useState([]);
  const [account, setAccount] = useState(undefined); // undefined = unknown, null = none
  const [counts, setCounts] = useState({});
  const [error, setError] = useState(null);
  const beginFleet = useLatest();
  const beginSchemas = useLatest();
  const beginTokens = useLatest();

  const load = useCallback(async () => {
    const isCurrent = beginFleet();
    const q = activeGroupId ? `?group_id=${activeGroupId}` : '';
    // A new space starts blank: the last space's fleet is not this one's.
    setRows(null);
    setPriced([]);
    setAccount(undefined);
    setError(null);
    setCounts(({ schemas, tokens }) => ({ schemas, tokens }));
    try {
      // my-apps is the one call that must succeed; the signals (users,
      // scores, payout account) each fail on their own as "unknown"
      // (utils/fleetFacts.js), so a bad day for the grader never blanks Home.
      const [appsRes, signals] = await Promise.all([
        api.request(`/third-party/my-apps${q}`),
        loadFleetSignals(activeGroupId),
      ]);
      const apps = appsRes?.apps || [];

      const drafts = await Promise.all(apps.map((a) => (
        a.is_plugin
          ? Promise.resolve(null)
          : api.request(`/third-party/${a.id}/draft`).catch(() => null)
      )));
      if (!isCurrent()) return;

      setRows(apps.map((a, i) => describeApp(a, factsFor(a, signals, drafts[i]))).sort(byUrgency));
      setUsersKnown(!!signals.usersKnown);
      setPriced(apps.filter(isPaid));
      setAccount(signals.account);
      setCounts((c) => ({
        ...c,
        users: signals.summary?.total_real_users,
        testers: signals.summary?.total_test_users,
        calls: signals.summary?.total_api_calls_30d,
      }));
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err?.message || 'Could not load your apps.');
      setRows([]);
    }
  }, [activeGroupId, beginFleet]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const isCurrent = beginSchemas();
    setCounts((c) => ({ ...c, schemas: undefined }));
    const put = (patch) => isCurrent() && setCounts((c) => ({ ...c, ...patch }));
    // The schemas library's query (grouped by uri), narrowed the way its "My
    // schemas" filter narrows it: the registry lists every schema on the
    // platform, and this figure is the ones the active owner made.
    listAllSchemas(api, 'group_by_uri=true', { pageSize: 500 })
      .then((rows) => put({ schemas: ownSchemas(rows, userId, activeGroupId).length }))
      .catch(() => {});
  }, [userId, activeGroupId, beginSchemas]);

  useEffect(() => {
    const isCurrent = beginTokens();
    api.listDeveloperTokens()
      .then((t) => isCurrent() && setCounts((c) => ({ ...c,
        // live ones only: a revoked or expired token is history, not something you hold
        tokens: (t?.tokens || t || []).filter((x) => !x.is_revoked && !x.is_expired).length })))
      .catch(() => {});
  }, [beginTokens]);

  // Plugins are their own list, read only when there are no apps to decide
  // whether this space has anything built at all. undefined = unknown.
  const appsEmpty = rows !== null && rows.length === 0 && !error;
  const [pluginCount, setPluginCount] = useState(undefined);
  useEffect(() => {
    setPluginCount(undefined);
    if (!appsEmpty) return undefined;
    let live = true;
    api.getMyPlugins(activeGroupId || undefined)
      .then((list) => { if (live) setPluginCount((list || []).length); })
      .catch(() => { if (live) setPluginCount(null); });   // null = could not tell; stay on Home
    return () => { live = false; };
  }, [appsEmpty, activeGroupId]);

  const nothingBuilt = appsEmpty && pluginCount === 0;
  const onboarding = nothingBuilt ? readOnboarding(userId, activeGroupId) : null;
  const resumeAt = `/welcome?step=${onboarding?.step || 1}&kind=${onboarding?.kind === 'plugin' ? 'plugin' : 'app'}`;
  const welcomeDue = !!onboarding && !onboarding.state;
  useEffect(() => {
    if (welcomeDue) navigate(resumeAt, { replace: true });
  }, [welcomeDue, resumeAt, navigate]);
  const stepsLeft = STEPS - (onboarding?.step || 1) + 1;
  // Until the plugin list answers, an empty app list may still be a redirect:
  // say "Reading" rather than flash the empty Home this page exists to skip.
  const deciding = appsEmpty && pluginCount === undefined;

  const fix = useCallback((row) => {
    const b = row.blocking;
    if (b?.href) { navigate(b.href); return; }
    // Land on the panel that shows the thing in the way, not on Details.
    navigate(b?.panel ? `/applications/${row.id}?panel=${b.panel}` : appHref(row));
  }, [navigate]);

  const all = rows || [];
  const needs = all.filter((r) => r.blocking);
  const inHands = all.filter((r) => r.state.key === 'live' || r.state.key === 'charging').length;
  // Designing is read off the record; anything above it needs the counts.
  const stageKnown = (key) => usersKnown || key === 'designing';
  const unplaced = all.filter((r) => !stageKnown(r.state.key));

  if (deciding) {
    return (
      <div className="devhome">
        <header className="devhome__head">
          <h1 className="devhome__title">Your apps</h1>
          <p className="devhome__sub">Reading the fleet…</p>
        </header>
      </div>
    );
  }

  return (
    <div className="devhome">
      <header className="devhome__head">
        <h1 className="devhome__title">Your apps</h1>
        <p className="devhome__sub">
          {rows === null ? 'Reading the fleet…'
            : all.length === 0 ? 'Nothing registered yet.'
            : `${all.length} built, ${usersKnown ? inHands : '—'} in people's hands. ${needs.length ? `${needs.length} ${needs.length === 1 ? 'needs' : 'need'} you.` : 'Nothing is waiting on you.'}`}
        </p>
      </header>

      {onboarding?.state === 'skipped' && (
        <p className="devhome__resume">
          <Link to={resumeAt}>Finish setting up — {stepsLeft} {stepsLeft === 1 ? 'step' : 'steps'} left</Link>
        </p>
      )}

      {error && <p className="devhome__error">{error}</p>}

      {rows !== null && all.length === 0 && !error && (
        <div className="devhome__empty">
          <p>No apps yet. The first one takes about ten minutes.</p>
          <button type="button" className="devhome__go" onClick={() => navigate('/create?lane=new')}>
            Start developing <ArrowRight size={13} />
          </button>
        </div>
      )}

      {all.length > 0 && (
        <section className="devhome__lane" aria-label="Where your apps stand">
          {PIPELINE.map((st, i) => {
            const known = stageKnown(st.key);
            const list = known ? all.filter((r) => r.state.key === st.key) : [];
            return (
              <div key={st.key} className="devhome__stage" data-state={st.key}>
                <span className="devhome__stage-word"><span>0{i + 1}</span>{st.label}</span>
                <span className="devhome__stage-n" data-zero={list.length ? undefined : true}>{known ? list.length : '—'}</span>
                <span className="devhome__stage-about">{ABOUT[st.key]}</span>
                <div className="devhome__chips">
                  {list.map((r) => (
                    <button key={r.id} type="button" className="devhome__chip" onClick={() => navigate(appHref(r))}>
                      <span className="devhome__face" aria-hidden="true">{r.isPlugin ? <Plug size={12} /> : <Box size={12} />}</span>
                      <span className="devhome__chip-name">{r.name}</span>
                      {r.blocking && <span className="devhome__dot" title="Needs you" />}
                    </button>
                  ))}
                  {!list.length && <span className="devhome__none">{known ? 'None yet' : 'Unknown'}</span>}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {unplaced.length > 0 && (
        <p className="devhome__error" role="status">
          Could not read who has authorized your apps, so {unplaced.length === 1 ? 'one app\'s stage is' : `${unplaced.length} apps' stages are`} unknown
          {' '}({unplaced.map((r) => r.name).join(', ')}).
        </p>
      )}

      {needs.length > 0 && (
        <section className="devhome__sect" aria-label="Needs you">
          <div className="devhome__sect-head"><span className="devhome__sect-title">Needs you</span><span className="devhome__sect-aside">{needs.length} open</span></div>
          <div className="devhome__rows">
            {needs.map((r) => (
              <button key={r.id} type="button" className="devhome__row" onClick={() => fix(r)}>
                <span className="devhome__face is-md" aria-hidden="true">{r.isPlugin ? <Plug size={15} /> : <Box size={15} />}</span>
                <span className="devhome__row-words">
                  <span className="devhome__row-name">{r.name}</span>
                  <span className="devhome__row-sub">{r.blocking.text}</span>
                </span>
                <span className="devhome__go">{r.blocking.action} <ArrowRight size={13} /></span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="devhome__figures">
        <Figure
          value={counts.users}
          label="People who authorized your apps"
          note={counts.testers > 0 ? `${counts.testers} test ${counts.testers === 1 ? 'user' : 'users'} too` : undefined}
          onPress={() => navigate('/applications/metrics')}
        />
        <Figure
          value={counts.calls === undefined ? undefined : counts.calls.toLocaleString()}
          label="API calls, 30 days"
          onPress={() => navigate('/applications/metrics')}
        />
        <Figure value={counts.schemas} label="Schemas" onPress={() => navigate('/schemas')} />
        <Figure value={counts.tokens} label="Developer tokens" onPress={() => navigate('/tokens')} />
      </div>

      {account !== undefined && (
        <section className="devhome__sect" aria-label="Getting paid">
          <div className="devhome__sect-head">
            <span className="devhome__sect-title">Getting paid</span>
            <button type="button" className="devhome__link" onClick={() => navigate('/billing')}>Money</button>
          </div>
          <PayoutChecklist account={account} pricedApps={priced} onGo={navigate} />
        </section>
      )}

      <section className="devhome__sect" aria-label="Start something">
        <div className="devhome__sect-head"><span className="devhome__sect-title">Start something</span></div>
        <div className="devhome__starts">
          {[
            { icon: Box, label: 'New app', sub: 'A token, one paste for your assistant, then watch it arrive.', to: '/create?lane=new' },
            { icon: Plug, label: 'New plugin', sub: 'A tool agents can run on someone\'s data.', to: '/plugins/new' },
            { icon: Terminal, label: 'Developer token', sub: 'For the CLI and CI. Scoped and expiring.', to: '/tokens' },
            { icon: BookOpen, label: 'Quickstart', sub: 'From zero to a granted test user in ten minutes.', to: '/documentation/quickstart' },
          ].map(({ icon: Icon, label, sub, to }) => (
            <button key={label} type="button" className="devhome__start" onClick={() => navigate(to)}>
              <Icon size={18} strokeWidth={1.6} />
              <span className="devhome__start-name">{label}</span>
              <span className="devhome__start-sub">{sub}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
